import { forwardRef } from "react";
import { Sparkles } from "lucide-react";
import type { Item, WithId } from "@um/contracts";

import { ItemImage } from "@um/ui/components";
import { isEquippableItem, itemKindLabels, itemKindOf } from "@um/contracts";
import { isRare, SLOT_LABELS, slotOf } from "./looks";

/** The plate an item is presented on. */
export const ItemPlate = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string }
>(function ItemPlate({ children, className = "" }, ref) {
  return (
    <div
      ref={ref}
      className={`relative mx-auto flex items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-brand-mint via-white to-white ring-1 ring-black/5 ${className}`}
    >
      {children}
    </div>
  );
});

/** The item's own art, sized to sit inside `ItemPlate`. */
export function ItemArt({
  item,
  className = "h-40 w-40",
}: {
  item: WithId<Item>;
  className?: string;
}) {
  return (
    // No `animate-in fade-in zoom-in-95`.
    <ItemImage
      image={item.image}
      alt={item.name}
      className={`${className} object-contain`}
    />
  );
}

/** What it is and whether it's rare — the two facts worth a badge. */
export function ItemBadges({ item }: { item: WithId<Item> }) {
  return (
    <p className="flex items-center justify-center gap-1.5 text-center text-sm text-gray-600">
      <span className="rounded-full bg-brand-mint px-2 py-0.5 text-xs font-medium text-brand-teal">
        {isEquippableItem(item) ? SLOT_LABELS[slotOf(item)] : itemKindLabels[itemKindOf(item)]}
      </span>
      {isRare(item) && (
        <span className="flex items-center gap-1 rounded-full bg-brand-lavender px-2 py-0.5 text-xs font-semibold text-brand-purple">
          <Sparkles className="h-3 w-3" aria-hidden />
          Rare
        </span>
      )}
      {item.earnedVia === "photo" && (
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
          Created by you
        </span>
      )}
    </p>
  );
}
